import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTimelineDecisoes, useContratosTimeline, getActionLabel, type TimelineEvent } from '@/hooks/useTimelineDecisoes';
import { TimelineFilters } from '@/components/empreendimentos/timeline/TimelineFilters';
import { TimelineDateGroups } from '@/components/empreendimentos/timeline/TimelineDateGroup';
import { TimelineDetailSheet } from '@/components/empreendimentos/timeline/TimelineDetailSheet';
import { TimelineStats } from '@/components/empreendimentos/timeline/TimelineStats';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Loader2, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { writeExcel } from '@/lib/excelEngine';

function formatDate(d: string) {
  try {
    return format(new Date(d), "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return d;
  }
}

const TimelineDecisoesPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [contratoId, setContratoId] = useState<string>('all');
  const [tipoAcao, setTipoAcao] = useState<string>('all');
  const [tipoEvento, setTipoEvento] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: contratos = [] } = useContratosTimeline();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTimelineDecisoes({
    contratoId: contratoId !== 'all' ? contratoId : undefined,
    tipoAcao: tipoAcao !== 'all' ? tipoAcao : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  // Flatten pages
  const allEvents = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(p => p.events);
  }, [data]);

  // Client-side filtering for type and text search
  const filteredEvents = useMemo(() => {
    let events = allEvents;
    if (tipoEvento !== 'all') {
      events = events.filter(e => e.tipo === tipoEvento);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      events = events.filter(e =>
        (e.descricao?.toLowerCase().includes(q)) ||
        (e.usuario_nome?.toLowerCase().includes(q)) ||
        (e.usuario_email?.toLowerCase().includes(q)) ||
        (e.observacoes?.toLowerCase().includes(q)) ||
        (e.documento_ref?.toLowerCase().includes(q)) ||
        (e.entidade?.toLowerCase().includes(q))
      );
    }
    return events;
  }, [allEvents, tipoEvento, searchText]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!bottomRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) fetchNextPage(); },
      { rootMargin: '200px' }
    );
    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const handleSelectEvent = useCallback((event: TimelineEvent) => {
    setSelectedEvent(event);
    setDetailOpen(true);
  }, []);

  // Export CSV/Excel
  const handleExportExcel = useCallback(() => {
    if (!filteredEvents.length) return;
    const rows = filteredEvents.map(e => ({
      'Data': formatDate(e.created_at),
      'Tipo': e.tipo,
      'Ação': getActionLabel(e.acao),
      'Descrição': e.descricao,
      'Usuário': e.usuario_nome || '',
      'Email': e.usuario_email || '',
      'Documento': e.documento_ref || '',
      'Output': e.output_gerado || '',
      'Observações': e.observacoes || '',
      'Crítico': e.is_critical ? 'Sim' : 'Não',
    }));
    writeExcel(
      [{ name: 'Timeline', data: rows }],
      `timeline-contratual-${new Date().toISOString().slice(0, 10)}`
    );
  }, [filteredEvents]);

  const handleExportPDF = useCallback(() => {
    // Use browser print as lightweight PDF generation
    const printContent = filteredEvents.map(e =>
      `${formatDate(e.created_at)} | ${getActionLabel(e.acao)} | ${e.descricao} | ${e.usuario_nome || '—'} | ${e.usuario_email || '—'}`
    ).join('\n');

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`
      <html><head><title>Timeline Contratual</title>
      <style>body{font-family:sans-serif;font-size:12px;padding:20px}h1{font-size:16px}
      table{width:100%;border-collapse:collapse;margin-top:12px}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;font-size:11px}
      th{background:#f5f5f5;font-weight:600}</style></head><body>
      <h1>Timeline de Decisões Contratuais</h1>
      <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
      <table><thead><tr><th>Data</th><th>Ação</th><th>Descrição</th><th>Usuário</th><th>Email</th><th>Documento</th></tr></thead><tbody>
      ${filteredEvents.map(e => `<tr><td>${formatDate(e.created_at)}</td><td>${getActionLabel(e.acao)}</td><td>${e.descricao}</td><td>${e.usuario_nome || '—'}</td><td>${e.usuario_email || '—'}</td><td>${e.documento_ref || '—'}</td></tr>`).join('')}
      </tbody></table></body></html>
    `);
    w.document.close();
    w.print();
  }, [filteredEvents]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <TimelineFilters
        searchText={searchText}
        onSearchTextChange={setSearchText}
        contratoId={contratoId}
        onContratoChange={setContratoId}
        tipoEvento={tipoEvento}
        onTipoEventoChange={setTipoEvento}
        tipoAcao={tipoAcao}
        onTipoAcaoChange={setTipoAcao}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
        contratos={contratos}
        onExportCSV={handleExportExcel}
        onExportPDF={handleExportPDF}
        hasEvents={filteredEvents.length > 0}
      />

      {/* Stats */}
      {allEvents.length > 0 && (
        <TimelineStats events={filteredEvents} totalLoaded={filteredEvents.length} />
      )}

      {/* Content */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertCircle className="w-10 h-10 text-destructive/60 mb-2" />
          <p className="text-sm text-muted-foreground">Erro ao carregar timeline</p>
        </div>
      )}

      {!isLoading && !error && filteredEvents.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Clock className="w-10 h-10 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum evento encontrado</p>
        </div>
      )}

      {!isLoading && !error && filteredEvents.length > 0 && (
        <ScrollArea className="pr-2">
          <TimelineDateGroups events={filteredEvents} onSelectEvent={handleSelectEvent} />

          {/* Infinite scroll sentinel */}
          <div ref={bottomRef} className="py-4 flex justify-center">
            {isFetchingNextPage && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
            {!hasNextPage && allEvents.length > 50 && (
              <p className="text-xs text-muted-foreground">Todos os eventos carregados</p>
            )}
          </div>
        </ScrollArea>
      )}

      {/* Detail side panel */}
      <TimelineDetailSheet
        event={selectedEvent}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
};

export default TimelineDecisoesPage;
